import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";

export type AnyPickedAsset = DocumentPickerAsset | ImagePickerAsset;

export const ANONYMOUS_USER_ATTACHMENT_LIMIT = 3;

export const exceedsAnonymousAttachmentLimit = (
  isSignedIn: boolean | undefined,
  attachmentCount: number,
  newAttachmentCount: number,
): boolean =>
  isSignedIn === false &&
  attachmentCount + newAttachmentCount > ANONYMOUS_USER_ATTACHMENT_LIMIT;

/**
 * Returns a sensible filename for a picked asset, regardless of source.
 * - DocumentPickerAsset uses `name`
 * - ImagePickerAsset uses `fileName`
 */
export function getAssetFilename(
  asset: AnyPickedAsset,
  fallback = "Attachment",
): string {
  return (
    ("name" in asset && asset.name) ||
    ("fileName" in asset && asset.fileName) ||
    fallback
  );
}

const HEIC_MIME_TYPES = new Set(["image/heic", "image/heif"]);

export const isHeic = (mimeType?: string | null): boolean =>
  !!mimeType && HEIC_MIME_TYPES.has(mimeType.toLowerCase());

export const getFriendlyFileType = (mimeType?: string | null): string => {
  if (!mimeType) {
    return "Document";
  }
  switch (mimeType) {
    case "application/pdf":
      return "PDF";
    case "application/vnd.oasis.opendocument.presentation":
      return "Presentation";
    case "application/vnd.oasis.opendocument.spreadsheet":
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.ms-excel.sheet.macroenabled.12":
    case "text/tsv":
    case "text/csv":
      return "Spreadsheet";
    default:
      return "Document";
  }
};
